import type { ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import { ScreenerLogo } from "../Screener/ScreenerLogo";
import Logo from "../shared/Logo";
import { IdentityTag } from "./IdentityTag";

interface CompanyIdentityRowProps {
  /** The listing's symbol; absent for a company we couldn't match (initials tile). */
  symbol: string | null;
  name: string;
  /** Beside the ticker, e.g. the exchange. */
  exchange?: string | null;
  /** Neutral tags after the exchange: "Not covered", "No price yet"… */
  tags?: readonly string[];
  /** Replaces the name line. */
  secondLine?: ReactNode;
  /** A card's title: a larger logo, ticker and name. */
  large?: boolean;
}

/** The screener's company cell: logo, ticker with its tags, then one quiet line. */
export const CompanyIdentityRow = ({
  symbol,
  name,
  exchange,
  tags = [],
  secondLine,
  large = false,
}: CompanyIdentityRowProps) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      minWidth: 0,
      textAlign: "left",
    }}
  >
    {symbol !== null && (
      <ScreenerLogo symbol={symbol} name={name} size={large ? 40 : 36} />
    )}
    {symbol === null && (
      <Logo
        src={undefined}
        alt=""
        ticker={name}
        companyName={name}
        size={large ? 40 : 36}
      />
    )}
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "nowrap",
          alignItems: "center",
          columnGap: 0.75,
          height: large ? 22 : 20,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <Typography
          className="identity-ticker"
          noWrap
          sx={{
            minWidth: 0,
            flexShrink: 1,
            fontSize: large ? { xs: "0.9375rem", md: "1rem" } : "0.875rem",
            lineHeight: large ? "22px" : "20px",
            fontWeight: 700,
            color: "text.primary",
          }}
        >
          {symbol ?? name}
        </Typography>
        {exchange && <IdentityTag>{exchange}</IdentityTag>}
        {tags.map((tag) => (
          <IdentityTag key={tag}>{tag}</IdentityTag>
        ))}
      </Box>
      {secondLine === undefined && (
        <Typography
          noWrap
          title={name}
          sx={{
            fontSize: large ? { xs: "0.75rem", md: "0.8125rem" } : "0.75rem",
            color: "text.secondary",
            mt: 0.25,
          }}
        >
          {name}
        </Typography>
      )}
      {secondLine !== undefined && (
        <Typography
          noWrap
          component="div"
          data-testid="identity-second-line"
          sx={{
            fontSize: "0.75rem",
            color: "text.secondary",
            mt: 0.25,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {secondLine}
        </Typography>
      )}
    </Box>
  </Box>
);
